/**
 * 멤버 프로필 SVG: 최초 1회 `/profile/{id}.svg` fetch 후 IndexedDB + 메모리 Blob URL로 재사용.
 * 멤버 DB의 profile_image ID는 레이아웃 캐시에서 오고, 이 모듈은 앱 제공 정적 SVG만 캐시한다.
 * (앱 제공 SVG는 사용자 이미지 사용량에 포함되지 않음)
 */

import {
  DEFAULT_MEMBER_PROFILE_IMAGE,
  memberProfileImageSrc,
  normalizeProfileImageId,
} from './classMembersStorage';
import {
  isExternalProfileImageSource,
  isMemberProfileStorageImageRef,
} from './memberProfileDisplay';

const DB_NAME = 'ClassPlannerProfileImageCache';
const DB_VERSION = 3;
const STORE = 'svgs';

const memoryBlobUrlById = new Map<string, string>();
const inflightById = new Map<string, Promise<string | null>>();

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (db.objectStoreNames.contains('images')) {
        db.deleteObjectStore('images');
      }
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet(key: string): Promise<ArrayBuffer | undefined> {
  const db = await idbOpen();
  try {
    return await new Promise<ArrayBuffer | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve(r.result as ArrayBuffer | undefined);
    });
  } finally {
    db.close();
  }
}

async function idbPut(key: string, body: ArrayBuffer): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).put(body, key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

async function fetchSvgArrayBuffer(sourceUrl: string): Promise<ArrayBuffer | null> {
  try {
    const res = await fetch(sourceUrl, {
      method: 'GET',
      cache: 'force-cache',
      credentials: 'omit',
      mode: 'cors',
    });
    if (!res.ok) return null;
    const buffer = await res.arrayBuffer();
    if (buffer.byteLength === 0) return null;
    return buffer;
  } catch {
    return null;
  }
}

function bufferToBlobUrl(buffer: ArrayBuffer): string {
  return URL.createObjectURL(new Blob([buffer], { type: 'image/svg+xml' }));
}

/**
 * @returns Blob URL — 호출 측에서 revoke 하지 말 것(세션 동안 재사용).
 */
export async function getCachedProfileImageUrl(imageId?: string | null): Promise<string | null> {
  // 관리자 프로필 카드 등 외부 공개 URL은 이미 완성된 주소이므로 SVG IndexedDB 파이프라인을 건너뛴다
  if (isExternalProfileImageSource(imageId)) return String(imageId);
  // 사용자 프로필 카드(비공개 경로)는 signed URL로 별도 조회
  if (isMemberProfileStorageImageRef(imageId)) return null;
  const id = normalizeProfileImageId(imageId);
  const mem = memoryBlobUrlById.get(id);
  if (mem) return mem;

  let inflight = inflightById.get(id);
  if (!inflight) {
    inflight = (async (): Promise<string | null> => {
      try {
        const cached = await idbGet(id);
        if (cached && cached.byteLength > 0) {
          const blobUrl = bufferToBlobUrl(cached);
          memoryBlobUrlById.set(id, blobUrl);
          return blobUrl;
        }
      } catch {
        /* IDB 미지원·거부 */
      }

      const sourceUrl = memberProfileImageSrc(id);
      const buffer = await fetchSvgArrayBuffer(sourceUrl);
      if (!buffer) {
        if (id !== DEFAULT_MEMBER_PROFILE_IMAGE) {
          return getCachedProfileImageUrl(DEFAULT_MEMBER_PROFILE_IMAGE);
        }
        return null;
      }

      try {
        await idbPut(id, buffer);
      } catch {
        /* 저장 실패해도 표시는 가능 */
      }
      const blobUrl = bufferToBlobUrl(buffer);
      memoryBlobUrlById.set(id, blobUrl);
      return blobUrl;
    })().finally(() => {
      inflightById.delete(id);
    });
    inflightById.set(id, inflight);
  }
  return inflight;
}

/** 여러 프로필을 미리 IDB에 적재 (백그라운드) */
export function preloadProfileImages(imageIds: Array<string | null | undefined>): void {
  const unique = Array.from(
    new Set(
      imageIds.map((id) => (isExternalProfileImageSource(id) ? String(id) : normalizeProfileImageId(id)))
    )
  );
  void Promise.all(unique.map((id) => getCachedProfileImageUrl(id)));
}
